import type { ReactElement } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';

import { AuthCallbackPage } from '@/pages/common/auth-callback';
import { EducationPage } from '@/pages/common/education';
import { EducationInquiryPage } from '@/pages/common/education-inquiry';
import { HomePage } from '@/pages/common/home';
import { LoginPage } from '@/pages/common/login';
import { NotFoundPage } from '@/pages/common/not-found';
import { OnboardingPage } from '@/pages/common/onboarding';
import { PortfolioDetailPage } from '@/pages/common/portfolio-detail';
import { RejectVerificationPage } from '@/pages/common/reject-verification';
import { ResetPasswordPage } from '@/pages/common/reset-password';
import { SignupPage } from '@/pages/common/signup';
import { SurveyPage } from '@/pages/common/survey';
import { UnsubscribePage } from '@/pages/common/unsubscribe';
import { VerifyEmailPage } from '@/pages/common/verify-email';
import { MarketingConsentPage, PrivacyPage, RefundPage, TermsPage } from '@/pages/legal';
import { LibraryPage } from '@/pages/library/assets';
import { VideoDetailPage } from '@/pages/library/video-detail';
import { MyAccountPage } from '@/pages/my/account';
import { MyAssetsPage } from '@/pages/my/assets';
import { MyHomePage } from '@/pages/my/home';
import { MySettingsPage } from '@/pages/my/settings';

import { AdminPathGuard } from './layouts/admin-path-guard';
import { RootLayout } from './layouts/root-layout';

/**
 * 1차 오픈 라우팅 — 14. 1차 오픈 범위 §8.
 *
 * 커머스(스토어·번들·장바구니·결제·주문)·클래스·케이스·B2B 는 **뺐다.**
 *
 * 홈(`/`)은 한때 닫고 자료실로 넘겼다 — 판매형 섹션뿐이라 1차 오픈에는 빈 캐러셀만 남았다.
 * 2026-09 리디자인으로 홈이 히어로 한 섹션(자료실·교육·도입 문의 세 갈래 입구)이 되면서
 * 그 이유가 사라져 다시 연다. 로고(`오톱 홈`)와 404 의 「홈으로」가 이 랜딩에 닿는다.
 */
const router = createBrowserRouter([
  {
    // 주소 어디에든 `admin` 칸이 있으면 운영자 안내 — 사이트 틀(RootLayout)보다 바깥이라
    //   어드민 앱이 받았을 때와 같은 모양이 된다 (AdminPathGuard 주석).
    element: <AdminPathGuard />,
    children: [
      {
        element: <RootLayout />,
        children: [
          { path: '/', element: <HomePage /> },

          // 자료실 — 1차 오픈의 핵심 화면(히어로 첫 카드·주 CTA 가 여기로 보낸다).
          // 자료 상세 페이지는 두지 않는다 — 영상 상세(/library/:slug)가 같은 일을 더 잘 하고,
          // 다운로드는 별도 페이지가 아니라 게이트(모달·자동 재개)로 그 자리에서 끝난다 (D3).
          { path: '/library', element: <LibraryPage /> },
          { path: '/library/:slug', element: <VideoDetailPage /> },
          { path: '/my', element: <MyHomePage /> },
          { path: '/my/downloads', element: <MyAssetsPage /> },
          { path: '/my/settings', element: <MySettingsPage /> },
          { path: '/my/account', element: <MyAccountPage /> },

          // 가입 퍼널 — 받기 → 회원가입 → 온보딩 → 다운로드
          { path: '/login', element: <LoginPage /> },
          { path: '/signup', element: <SignupPage /> },
          { path: '/onboarding', element: <OnboardingPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },

          // 인증 메일이 닿는 자리. `/auth` 아래에 두지 않는다 — 그 접두사는 소셜 라우트가
          //   쓰고 프록시가 통째로 api 로 넘긴다(webpack.config.js · nginx.conf.template).
          { path: '/verify', element: <VerifyEmailPage /> },
          { path: '/reject', element: <RejectVerificationPage /> },

          // 소셜 로그인이 끝나고 브라우저가 되돌아오는 자리. api 가 이 경로로 redirect 하므로
          //   개발 프록시(webpack)와 운영 nginx 모두 /auth/** 중 이 경로만 SPA 로 넘긴다.
          { path: '/auth/callback', element: <AuthCallbackPage /> },

          // 리드가 들어오는 두 번째 문
          { path: '/education', element: <EducationPage /> },
          { path: '/education/inquiry', element: <EducationInquiryPage /> },
          { path: '/education/portfolio/:projectId', element: <PortfolioDetailPage /> },

          // AI 업무 자동화 수요 조사. 경로를 기존 설문(www.autop.kr/survey)과 같게 둔다 —
          //   도메인을 이 서버로 옮기는 순간, 이미 퍼진 설문 링크가 그대로 여기로 닿는다.
          { path: '/survey', element: <SurveyPage /> },

          // 법적 고지 — 공개 전 게시가 선행 조건
          { path: '/terms', element: <TermsPage /> },
          { path: '/privacy', element: <PrivacyPage /> },
          { path: '/marketing-consent', element: <MarketingConsentPage /> },
          // 메일 하단 수신거부 링크가 닿는 곳. 로그인 뒤로 숨기지 않는다 — 정보통신망법 제50조
          { path: '/unsubscribe', element: <UnsubscribePage /> },
          { path: '/refund', element: <RefundPage /> },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function AppRouter(): ReactElement {
  return <RouterProvider router={router} />;
}
